import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { type AlertItem, getAlerts, setGlobalAlertMute } from "../api/client";
import { NavBar } from "../components/NavBar";
import { LoadingState } from "../components/LoadingState";

/**
 * AlertsPage — Task 9.6 (frontend). Inbox of triggered alerts for the paid
 * user, with a global mute toggle. Per-property mute lives on the Saved
 * dashboard. Backend: GET /alerts + PUT /alerts/mute.
 */

const TYPE_LABEL: Record<string, string> = {
  permit: "New building permit",
  distress: "Distress signal",
  ownership: "Ownership change",
  avm_shift: "AVM shift",
  relisting: "Relisting",
};

export function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertItem[] | null>(null);
  const [globallyMuted, setGloballyMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAlerts()
      .then((r) => {
        if (!cancelled) {
          setAlerts(r.alerts);
          setGloballyMuted(r.globally_muted);
        }
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e.message || "Could not load alerts.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggleGlobalMute() {
    const next = !globallyMuted;
    setGloballyMuted(next); // optimistic
    try {
      await setGlobalAlertMute(next);
    } catch {
      setGloballyMuted(!next); // revert on failure
    }
  }

  return (
    <div className="app">
      <NavBar />
      <main>
        <header className="page-header">
          <h1>Alerts</h1>
          <label className="mute-toggle">
            <input
              type="checkbox"
              checked={globallyMuted}
              onChange={() => void toggleGlobalMute()}
            />
            Mute all alerts
          </label>
        </header>

        {error && <p className="error" role="alert">{error}</p>}
        {!alerts && !error && <LoadingState message="Loading alerts…" />}

        {alerts && alerts.length === 0 && (
          <p className="empty-state">
            No alerts yet. Save a property to start monitoring it for new
            permits, distress filings, ownership changes, and AVM shifts.
          </p>
        )}

        {alerts && alerts.length > 0 && (
          <ul className="alert-list">
            {alerts.map((a) => (
              <li key={a.alert_id} className={`alert-row alert-${a.alert_type}`}>
                <div className="alert-type-badge">
                  {TYPE_LABEL[a.alert_type] ?? a.alert_type}
                </div>
                <div className="alert-main">
                  <div className="alert-address">
                    {a.score_id ? (
                      <Link to={`/score/${a.score_id}`}>{a.address ?? "View Tilt Report"}</Link>
                    ) : (
                      a.address ?? a.place_id
                    )}
                  </div>
                  <div className="alert-summary">{a.summary}</div>
                </div>
                <div className="alert-date">
                  {new Date(a.detected_at).toLocaleDateString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
